import type { MantineTheme } from '@mantine/core';
import {
	Box,
	Center,
	CopyButton,
	createStyles,
	Group,
	Textarea,
	TextInput,
	Tooltip,
	UnstyledButton,
} from '@mantine/core';
import { useInputState } from '@mantine/hooks';
import type { TeamOut } from '@repo/api-codegen';
import { useApiListTeamsV2 } from '@repo/api-codegen/v2';
import RichTooltip from '@repo/common/components/RichTooltip/RichTooltip';
import { EntityType } from '@repo/common/enums/entityType';
import { UserRole } from '@repo/common/enums/UserRole';
import { Badge, Text } from '@repo/foundations';
import { typography } from '@repo/theme/primitives';
import { observer } from 'mobx-react-lite';
import type { ChangeEvent } from 'react';
import { useEffect, useRef } from 'react';
import { useIntegration, type ISecodaEntity } from '../../../api';
import type { SecodaEntity } from '../../../lib/models';
import { getEntityDisplayTitle } from '../../../lib/utils/entity';
import { useCheckIAMPermission } from '../../../utils/authorization/roles';

export interface IEntityPageTitleProps {
	placeholder: string;
	icon?: React.ReactNode;
	entity:
		| SecodaEntity
		| ISecodaEntity
		| { title: string; title_cased?: string };
	isReadOnly: boolean;
	onChange: (title: string) => void;
	wrapTitle?: boolean;
	size?: keyof typeof typography.title;
}

const useStyles = createStyles(
	(theme: MantineTheme, size: keyof typeof typography.title) => ({
		textAreaRoot: {
			flex: 1,
			minWidth: 0, // Prevent collapse when empty
		},
		textAreaInput: {
			fontSize: typography.text[size],
			fontWeight: typography.weight.bold,
			lineHeight: '32px',
			padding: 0,
			overflowY: 'hidden',
			minWidth: 0,

			'&:focus': {
				boxShadow: 'none',
				borderColor: 'transparent',
				backgroundColor: 'transparent',
			},

			'&:disabled': {
				opacity: 1,
				backgroundColor: theme.other.getColor('fill/transparent/default'),
				color: theme.other.getColor('text/primary/default'),
				cursor: 'text',
			},
		},
		titleButton: {
			maxWidth: '100%',
			flex: '1 1 auto',
			minWidth: 0,
		},
		titleGroup: {
			flex: '1 1 auto',
			minWidth: 0,
		},
	})
);

function EntityPageTitle({
	placeholder = 'Untitled',
	icon,
	entity,
	isReadOnly,
	onChange,
	wrapTitle = false,
	size = 'xxl',
}: IEntityPageTitleProps) {
	const { classes } = useStyles(size);
	const { hasPermission } = useCheckIAMPermission({
		v1AllowedRoles: [UserRole.ADMIN, UserRole.EDITOR],
		v2Permission: 'Resources.Update',
		entityId: 'id' in entity ? entity.id : undefined,
	});

	const readOnlyUser = !hasPermission;

	const showReadOnlyBadge = !hasPermission;

	const { data: userTeams } = useApiListTeamsV2({
		queryParams: {
			only_joined: true,
		},
	});

	const { data: entityIntegration } = useIntegration({
		id: 'integration' in entity && entity.integration ? entity.integration : '',
		options: {
			enabled: 'integration' in entity && !!entity.integration,
		},
	});

	const entityTeamIds = [
		...('teams' in entity ? entity.teams || [] : []),
		...(entityIntegration?.teams || []),
	];
	const userTeamIds = userTeams?.map((team: TeamOut) => team.id);

	// Only show exploring badge if entity supports teams and user has no overlapping teams
	const entitySupportsTeams =
		'teams' in entity || !!entityIntegration?.teams?.length;
	const isExploring =
		entitySupportsTeams &&
		userTeamIds?.length &&
		entityTeamIds.length &&
		userTeamIds.every((teamId: string) => !entityTeamIds.includes(teamId));

	const inputTitleRef = useRef<HTMLInputElement>(null);

	// Set title to empty string if it is "Untitled" or "Untitled (n) where n is a positive integer
	const [title, setTitle] = useInputState(
		/^Untitled(\s*\(\d+\))?$/.test(entity.title ?? '')
			? ''
			: (entity.title ?? '')
	);

	useEffect(() => {
		if (!isReadOnly && !readOnlyUser && title === '') {
			inputTitleRef.current?.focus();
		}
	}, [isReadOnly, readOnlyUser, title, entity.title]);

	const handleChange = (
		event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
	) => {
		const { value } = event.currentTarget;
		setTitle(value);
		onChange(value);
	};

	const casedTitle = getEntityDisplayTitle(entity);
	let copyText = casedTitle;
	if (
		'id' in entity &&
		[EntityType.table, EntityType.column].includes(entity.entity_type)
	) {
		copyText = entity.title_full ?? casedTitle;
	}

	const copiedText = `"${copyText}" copied to clipboard`;

	return isReadOnly || readOnlyUser ? (
		<CopyButton value={copyText} timeout={2000}>
			{({ copied, copy }) => (
				<Group spacing="sm" align="center" noWrap>
					{icon && <Center>{icon}</Center>}
					<Group
						spacing="sm"
						align="baseline"
						noWrap
						className={classes.titleGroup}
					>
						<Tooltip
							label={<Text size="xs">{copied ? copiedText : copyText}</Text>}
							position="top-start"
							offset={-10}
						>
							<UnstyledButton
								variant="subtle"
								onClick={copy}
								className={classes.titleButton}
							>
								<Text
									data-testid="input-title"
									size={size}
									weight="bold"
									truncate
								>
									{casedTitle}
								</Text>
							</UnstyledButton>
						</Tooltip>
						{showReadOnlyBadge && (
							<Badge style={{ flex: 'none' }} variant="default">
								Read Only
							</Badge>
						)}
						{isExploring && (
							<RichTooltip
								title="Exploring"
								body="You don't belong to any of this resource's teams."
							>
								<Badge style={{ flex: 'none' }} variant="default">
									Exploring
								</Badge>
							</RichTooltip>
						)}
					</Group>
				</Group>
			)}
		</CopyButton>
	) : (
		<Group spacing="sm" align="flex-start" noWrap style={{ flex: 1 }}>
			{icon && (
				<Box h={wrapTitle ? 32 : 42}>
					<Center h="100%">{icon}</Center>
				</Box>
			)}
			{wrapTitle ? (
				<Textarea
					classNames={{
						root: classes.textAreaRoot,
						input: classes.textAreaInput,
					}}
					data-testid="input-title"
					value={title}
					onChange={handleChange}
					placeholder={placeholder}
					variant="unstyled"
					autosize
					autoFocus={!isReadOnly && !readOnlyUser && title === ''}
					size="md"
				/>
			) : (
				<TextInput
					ref={inputTitleRef}
					classNames={{
						root: classes.textAreaRoot,
						input: classes.textAreaInput,
					}}
					data-testid="input-title"
					value={title}
					onChange={handleChange}
					placeholder={placeholder}
					variant="unstyled"
					size="md"
				/>
			)}
			{isExploring && (
				<RichTooltip
					title="Exploring"
					body="You don't belong to any of this resource's teams."
				>
					<Badge style={{ flex: 'none' }} variant="default">
						Exploring
					</Badge>
				</RichTooltip>
			)}
		</Group>
	);
}

const EntityPageTitleObserver = observer(EntityPageTitle);
export default EntityPageTitleObserver;
